import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import * as Sentry from '@sentry/react';
import type { ConnectionHookResult, ProviderCode } from '@interfaces/Integration';
import { PROVIDER_TYPE_MAP } from '@utils/constants';
import { useSyncIntegration, useRemoveIntegration, useGetIntegrations } from '../../../../hooks';
import {
  GOOGLE_ACTIONS,
  type GoogleBusinessConfiguration,
  type GoogleLinkMode,
  type GooglePlaceCandidate,
  type IntegrationHealth,
} from '../types';

interface UseGoogleBusinessConnectionProps {
  id?: string;
  provider_code: ProviderCode;
  provider_type: keyof typeof PROVIDER_TYPE_MAP;
  configuration: Record<string, unknown>;
}

export interface GoogleBusinessConnectionResult extends ConnectionHookResult {
  health: IntegrationHealth | null;
  linkMode: GoogleLinkMode | null;
  profile: GoogleBusinessConfiguration;
  candidates: GooglePlaceCandidate[];
  isSearching: boolean;
  searchPlaces: (query: string) => Promise<void>;
  clearCandidates: () => void;
  connectWithPlaceId: (placeId: string) => Promise<void>;
  recheckStatus: () => Promise<void>;
}

const asConfig = (raw?: Record<string, unknown>): GoogleBusinessConfiguration =>
  (raw || {}) as GoogleBusinessConfiguration;

// Conexión con Google Business Profile.
//
// Dos decisiones que se apartan del resto de los hooks de integración:
//
// 1. El estatus se verifica contra Google, no se asume. Los demás hooks
//    inicializan `isConnected` con `Boolean(integration?.id)` y nunca revalidan.
//    Aquí el SHOW_STATUS inicial pregunta al proveedor, y `health` refleja la
//    respuesta real. Es el punto entero del rediseño: una ficha que perdimos por
//    permisos revocados debe verse rota, no verde.
//
// 2. El estado de conexión NO se cae ante un error de red. Si el probe falla por
//    timeout, la integración sigue vinculada — lo que cambia es su salud.
//    Marcarla "desconectada" haría que el comercio intente reconectar algo que
//    nunca se desconectó.
export const useGoogleBusinessConnection = (
  integration?: UseGoogleBusinessConnectionProps,
): GoogleBusinessConnectionResult => {
  const { syncIntegration } = useSyncIntegration();
  const { removeIntegration } = useRemoveIntegration();
  const { refreshIntegrations } = useGetIntegrations();

  const initialConfig = asConfig(integration?.configuration);

  const [profile, setProfile] = useState<GoogleBusinessConfiguration>(initialConfig);
  const [health, setHealth] = useState<IntegrationHealth | null>(initialConfig.health ?? null);
  const [isConnected, setIsConnected] = useState(Boolean(integration?.id && initialConfig.link_mode));
  const [isConnecting, setIsConnecting] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [candidates, setCandidates] = useState<GooglePlaceCandidate[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Evita que un probe en vuelo escriba estado después de desmontar o de que el
  // comercio ya haya desconectado — una carrera que dejaría la UI mintiendo.
  const isMountedRef = useRef(true);
  const probedIdRef = useRef<string | null>(null);

  // El body TIENE que re-marcar `true`: en StrictMode React monta → desmonta →
  // remonta, y sin esto el cleanup del primer ciclo deja el ref en `false` para
  // siempre. Todos los `setState` guardados quedan muertos y la UI se congela
  // (el caso visible: `isSearching` clavado y el botón "Buscar" deshabilitado).
  // No es solo dev: cualquier remount en producción lo reproduce.
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // useMemo + dependencias reales: sin esto el objeto se reconstruía en cada
  // render y los useCallback de abajo —que no lo declaraban— capturaban el del
  // PRIMER render. Tras un CONNECT exitoso la integración cambia de id, y las
  // acciones siguientes seguían cerrando sobre el id viejo.
  const providerRef = useMemo(
    () => ({
      id: integration?.id,
      provider_code: integration?.provider_code as ProviderCode,
      provider_type: integration?.provider_type ?? 'marketing',
    }),
    [integration?.id, integration?.provider_code, integration?.provider_type],
  );

  const applyResponse = useCallback((response: Record<string, unknown>) => {
    const config = asConfig(
      (response?.configuration as Record<string, unknown>) ?? (response?.config as Record<string, unknown>),
    );
    if (!isMountedRef.current) return config;

    setProfile((prev) => ({ ...prev, ...config }));
    if (config.health) setHealth(config.health);
    return config;
  }, []);

  /** Probe real contra Google. */
  const recheckStatus = useCallback(async () => {
    if (!integration?.id) return;

    setIsCheckingStatus(true);
    try {
      const response = await syncIntegration(providerRef as never, {
        action: GOOGLE_ACTIONS.SHOW_STATUS,
        notify: false,
      });
      const config = applyResponse(response);
      if (isMountedRef.current) setIsConnected(Boolean(config.link_mode));
    } catch (error) {
      // Un probe fallido NO desconecta: solo significa que no pudimos verificar.
      Sentry.captureException(error);
      if (isMountedRef.current) {
        setHealth((prev) =>
          prev ? { ...prev, state: 'UNREACHABLE', message: 'No pudimos verificar el estado con Google.' } : prev,
        );
      }
    } finally {
      if (isMountedRef.current) setIsCheckingStatus(false);
    }
    // providerRef se reconstruye en cada render; las dependencias reales son estas.
  }, [integration?.id, applyResponse, syncIntegration, providerRef]);

  /** Probe inicial, una sola vez por integración. */
  useEffect(() => {
    if (!integration?.id) return;
    if (probedIdRef.current === integration.id) return;
    probedIdRef.current = integration.id;
    recheckStatus();
  }, [integration?.id, recheckStatus]);

  /** Busca la ficha por texto libre. */
  const searchPlaces = useCallback(
    async (query: string) => {
      const trimmed = query.trim();
      if (trimmed.length < 3) {
        toast.error('Escribe al menos 3 caracteres para buscar tu negocio.');
        return;
      }

      setIsSearching(true);
      try {
        const response = await syncIntegration(providerRef as never, {
          action: GOOGLE_ACTIONS.SEARCH_PLACE,
          payload: { query: trimmed },
          notify: false,
        });
        const results = (response?.results as GooglePlaceCandidate[]) || [];
        if (isMountedRef.current) setCandidates(results);
        if (!results.length) toast.error('No encontramos tu negocio en Google. Prueba con la dirección.');
      } catch (error) {
        Sentry.captureException(error);
        // El mensaje del backend ya es específico (p. ej. Places sin configurar);
        // preferirlo sobre un genérico es la diferencia entre accionable y no.
        const message =
          (error as { body?: { message?: string } })?.body?.message || 'No pudimos buscar tu negocio en Google.';
        toast.error(message);
      } finally {
        if (isMountedRef.current) setIsSearching(false);
      }
    },
    [syncIntegration, providerRef],
  );

  const clearCandidates = useCallback(() => setCandidates([]), []);

  /** Vincula la ficha elegida (modo de solo lectura). */
  const connectWithPlaceId = useCallback(
    async (placeId: string) => {
      setIsConnecting(true);
      try {
        const response = await syncIntegration(providerRef as never, {
          action: GOOGLE_ACTIONS.CONNECT,
          payload: { link_mode: 'PLACE_ID' as GoogleLinkMode, place_id: placeId },
          notify: false,
        });
        const config = applyResponse(response);

        if (isMountedRef.current) {
          setIsConnected(true);
          setCandidates([]);
        }
        refreshIntegrations();
        toast.success(config.title ? `Ficha vinculada: ${config.title}` : 'Ficha de Google vinculada.');
      } catch (error) {
        Sentry.captureException(error);
        const message =
          (error as { body?: { message?: string } })?.body?.message || 'No pudimos vincular la ficha de Google.';
        toast.error(message);
      } finally {
        if (isMountedRef.current) setIsConnecting(false);
      }
    },
    [applyResponse, refreshIntegrations, syncIntegration, providerRef],
  );

  /**
   * Entrada genérica del contrato `ConnectionHookResult`: es lo que dispara el
   * botón "Conectar" de la tarjeta.
   *
   * A diferencia de un OAuth clásico, aquí no se redirige a ningún lado: el
   * comercio primero tiene que ELEGIR cuál de las fichas de Google es la suya,
   * así que se abre el modal de búsqueda. `Card.tsx` renderiza el modal contra
   * `isQrVisible`/`onCloseQr` del contrato compartido — de ahí el alias.
   */
  const connect = useCallback(() => {
    setCandidates([]);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setCandidates([]);
  }, []);

  const disconnect = useCallback(async () => {
    if (!integration?.id) return;

    setIsConnecting(true);
    try {
      // DISCONNECT primero: revoca el token en Google y limpia la config. Borrar
      // la fila sin revocar dejaría a Plick con acceso permanente a la ficha de
      // un comercio que pidió desconectarse.
      await syncIntegration(providerRef as never, { action: GOOGLE_ACTIONS.DISCONNECT, notify: false });
      // removeIntegration recibe la integración completa (la usa para actualizar
      // el store), no solo el id.
      await removeIntegration({ ...providerRef, configuration: {} } as never, false);

      if (isMountedRef.current) {
        setIsConnected(false);
        setProfile({});
        setHealth(null);
      }
      refreshIntegrations();
      toast.success('Ficha de Google desvinculada.');
    } catch (error) {
      Sentry.captureException(error);
      toast.error('No pudimos desvincular la ficha de Google.');
    } finally {
      if (isMountedRef.current) setIsConnecting(false);
    }
  }, [integration?.id, refreshIntegrations, removeIntegration, syncIntegration, providerRef]);

  return {
    isConnected,
    isConnecting,
    isCheckingStatus,
    connect,
    disconnect,
    // Alias del contrato compartido: `Card.tsx` monta el ConnectionModal contra
    // estas dos claves, pensadas para el QR de WhatsApp. Aquí gobiernan el modal
    // de búsqueda de ficha.
    isQrVisible: isModalOpen,
    onCloseQr: closeModal,
    status: health?.state,
    health,
    linkMode: profile.link_mode ?? null,
    profile,
    candidates,
    isSearching,
    searchPlaces,
    clearCandidates,
    connectWithPlaceId,
    recheckStatus,
  };
};
